import {OtsDateTime} from "../../components/OtsDateTime";
import {
  Message,
  MessageType,
  useCreateMessageMutation,
  useGetAllMessagesByObjectQuery,
} from "@ots-frontend/ots-data";
import {ChatBubbleLeftRightIcon} from "@heroicons/react/20/solid";
import {skipToken} from "@reduxjs/toolkit/query";
import {ReactNode, useContext, useMemo, useState} from "react";
import {AuthContext} from "../../app/AuthProvider";
import {OtsBadge, OtsBadgeTone} from "../../components/OtsBadge";
import {OtsActionButton} from "../../components/OtsButton";
import {OtsMessageList} from "../../components/OtsMessageList";
import {CommentDialog} from "../../dialogs/CommentDialog";

export type EntityCommentsProps = {
  objectId?: string;
  objectType?: string;
  tenantCode?: string;
  /** Message codes to display. Matching is case-insensitive and treats COMMENT/COMMENTS as equivalent. */
  messageTypes?: string[];
  density?: "compact" | "default";
  title?: ReactNode;
  description?: ReactNode;
  ariaLabel?: string;
  emptyState?: ReactNode;
  errorState?: ReactNode;
  showMessageTypes?: boolean;
  /** Shows the built-in add action and shared comment dialog. Off until the host grants it. */
  allowAddComment?: boolean;
  addCommentLabel?: ReactNode;
  commentDialogTitle?: string;
  commentMessageType?: string;
  allowCommentTypeChange?: boolean;
  commentTypeChoices?: MessageType[];
  commentReport?: boolean;
  commentMaxLength?: number;
  personId?: string;
  onCommentCreated?: () => void;
  /** Overrides the built-in dialog when a host needs a custom comment workflow. */
  onAddComment?: () => void;
};

const canonicalMessageType = (value: string) => {
  const normalized = value.trim().replace(/_/g, " ").toUpperCase();
  return normalized === "COMMENTS" ? "COMMENT" : normalized;
};
const formatMessageType = (value: string) => canonicalMessageType(value)
  .toLowerCase()
  .replace(/(^|\s)\S/g, character => character.toUpperCase());
const messageTypeTone = (value: string): OtsBadgeTone => canonicalMessageType(value) === "INTERNAL" ? "warning" : "neutral";
const mutationErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error != null) {
    const data = "data" in error ? (error as {data?: unknown}).data : undefined;
    if (typeof data === "object" && data != null && "detail" in data && typeof data.detail === "string") return data.detail;
    if ("error" in error && typeof (error as {error?: unknown}).error === "string") return (error as {error: string}).error;
  }
  return "The comment could not be added. Please try again.";
};

export const filterEntityMessages = (messages: Message[], messageTypes?: string[]): Message[] => {
  if (!messageTypes?.length) return messages;
  const requested = new Set(messageTypes.map(canonicalMessageType));
  return messages.filter(message => requested.has(canonicalMessageType(message.messageType)));
};

/** Data-connected, reusable comments section for any entity supported by the message API. */
export const EntityComments = ({
  objectId,
  objectType,
  tenantCode,
  messageTypes,
  density = "default",
  title = "Messages and comments",
  description = "Operational notes recorded against this entity.",
  ariaLabel = "Entity messages and comments",
  emptyState = "No messages or comments have been recorded.",
  errorState = "The messages could not be loaded.",
  showMessageTypes = true,
  allowAddComment = false,
  addCommentLabel = "Add comment",
  commentDialogTitle = "New Comment",
  commentMessageType,
  allowCommentTypeChange = false,
  commentTypeChoices,
  commentReport,
  commentMaxLength,
  personId,
  onCommentCreated,
  onAddComment,
}: EntityCommentsProps) => {
  const auth = useContext(AuthContext);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const query = useGetAllMessagesByObjectQuery(objectId ? {objectId, objectType, tenantCode} : skipToken);
  const [createMessage, createState] = useCreateMessageMutation();
  const messages = useMemo(
    () => [...filterEntityMessages(query.data ?? [], messageTypes)]
      .sort((left, right) => right.createDttm - left.createDttm),
    [messageTypes, query.data],
  );
  const newCommentMessageType = commentMessageType ?? messageTypes?.[0] ?? "Comment";
  const showAddComment = allowAddComment || onAddComment != null;
  const openAddComment = () => {
    if (onAddComment) {
      onAddComment();
      return;
    }
    setCreateError(undefined);
    setCommentDialogOpen(true);
  };
  const submitComment = async (comment: string | null, details: {messageType: string}) => {
    if (!comment || !objectId || !objectType) return;
    setCreateError(undefined);
    try {
      await createMessage({
        objectId,
        objectType,
        messageType: details.messageType,
        message: comment,
        report: commentReport,
        personId: personId ?? auth?.userId,
        tenantCode,
      }).unwrap();
      setCommentDialogOpen(false);
      onCommentCreated?.();
    } catch (error) {
      setCreateError(mutationErrorMessage(error));
    }
  };

  return <div>
    {(title != null || description != null || showAddComment) && <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        {title != null && <h2 className="text-base font-semibold text-[var(--ots-secondary)]">{title}</h2>}
        {description != null && <p className="mt-1 text-xs text-[var(--ots-text-muted)]">{description}</p>}
      </div>
      {showAddComment && <OtsActionButton
        size={density === "compact" ? "compact" : "default"}
        variant="secondary"
        icon={<ChatBubbleLeftRightIcon/>}
        disabled={!onAddComment && (!objectId || !objectType)}
        onClick={openAddComment}>
        {addCommentLabel}
      </OtsActionButton>}
    </div>}
    {query.isError
      ? <p
          role="alert"
          className={[
            "rounded-md border border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] px-3 py-2 text-sm text-[var(--ots-on-danger-container)]",
            title != null || description != null || showAddComment ? density === "compact" ? "mt-4" : "mt-6" : undefined,
          ].filter(Boolean).join(" ")}>
          {errorState}
        </p>
      : <OtsMessageList
          className={title != null || description != null || showAddComment ? density === "compact" ? "mt-4" : "mt-6" : undefined}
          ariaLabel={ariaLabel}
          density={density}
          loading={query.isLoading || query.isFetching}
          loadingState="Loading comments…"
          emptyState={emptyState}
          items={messages.map(message => ({
            id: message.id,
            authorName: message.personName,
            timestamp: <OtsDateTime value={message.createDttm} />,
            body: message.message,
            metadata: showMessageTypes ? <>
              <OtsBadge tone={messageTypeTone(message.messageType)} size="compact">{formatMessageType(message.messageType)}</OtsBadge>
              {message.report && <OtsBadge tone="info" size="compact">Reports</OtsBadge>}
            </> : undefined,
          }))}/>
    }
    <CommentDialog
      open={commentDialogOpen}
      title={commentDialogTitle}
      description="Add a note to this entity's comment history."
      submitLabel="Add comment"
      required
      maxLength={commentMaxLength}
      messageType={newCommentMessageType}
      allowMessageTypeChange={allowCommentTypeChange}
      messageTypes={commentTypeChoices}
      error={createError}
      isSubmitting={createState.isLoading}
      colorScheme="inherit"
      onSubmit={submitComment}
      onCancel={() => {
        setCreateError(undefined);
        setCommentDialogOpen(false);
      }}/>
  </div>;
};
